// Loan dashboard — vitals strip (Overview, above the fold). The LIVE health
// grade leads as the hero tile (server-computed from the same discipline
// lights as the scorecard — click-through scrolls to it; the chip popover
// carries the plain-language rubric), followed by the number-first metric
// tiles: current balance · next payment due · latest DSCR · days
// delinquent · account state · maturity countdown.
//
// Tints reuse the established conditions: delinquency ≥30d error / ≥1d
// warning, DSCR certificate status Pass/Watch/Fail. Next payment due comes
// from the v3 expected schedule (servicing ledger summary).
//
// Tile values render the FULL exact figure (never compact "$1.65M"
// notation); long values step the font size down instead of overflowing
// (valueSizeClass), with tabular-nums retained.

import { Skeleton, StatCard, StatsBar, StatusBadge } from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency } from "@/design-system/lib/format";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import type { ServicingLedgerSummaryDto } from "@/api/portfolioAdmin/servicingApi";
import type { LoanHealthDto } from "@/api/portfolioAdmin/loanHealthApi";
import { GradeChip } from "@/components/portfolioAdmin/loans/GradeChip";
import { daysUntil } from "../common/colorBand";

interface VitalsStripProps {
  loan: LoanSummaryDto;
  /** v3 ledger summary; undefined while loading, null when unavailable. */
  ledgerSummary: ServicingLedgerSummaryDto | null | undefined;
  ledgerLoading: boolean;
  /** Health payload for the hero grade; undefined while loading. */
  health: LoanHealthDto | undefined;
  healthLoading: boolean;
}

/** Step the value font down as the content grows so the full exact figure
 *  always fits the tile — never compact/truncated money. */
export function valueSizeClass(text: string): string {
  if (text.length <= 9) return "text-h3";
  if (text.length <= 13) return "text-h4";
  return "text-h5";
}

export function VitalsStrip({
  loan,
  ledgerSummary,
  ledgerLoading,
  health,
  healthLoading,
}: VitalsStripProps) {
  const delinquentClass =
    loan.daysDelinquent >= 30
      ? "text-status-error"
      : loan.daysDelinquent >= 1
        ? "text-status-warning"
        : "text-onyx";

  const dscrClass =
    loan.latestDscrStatus === "Fail"
      ? "text-status-error"
      : loan.latestDscrStatus === "Watch"
        ? "text-status-warning"
        : "text-onyx";

  const monthsToMaturity = monthsBetween(new Date(), new Date(loan.maturityDate));
  const balanceText = formatCurrency(loan.currentBalance);
  const daysText = String(loan.daysDelinquent);
  const monthsText = String(Math.max(monthsToMaturity, 0));

  return (
    // 🔴 CONTAINER-DRIVEN, AND IT SHEDS RATHER THAN JUMPING. This was
    // `grid-cols-2 md:grid-cols-4 xl:grid-cols-7` — three viewport
    // breakpoints, which answer for the WINDOW and not for the box this strip
    // is standing in. Measured before: seven tiles at a 1536px content box are
    // 205.7px each and hold their labels on one line; at 1440 the same seven
    // are 146.28px and `CURRENT BALANCE` / `NEXT PAYMENT DUE` wrap to two.
    // `StatsBar` at `columns={7}` with `STAT_TILE_MIN_WIDTH` 9.5rem sheds to
    // six and then five as the box narrows, so the label always has its line.
    <StatsBar columns={7} data-testid="loan-vitals-strip">
      <VitalTile label="Health grade" testId="vital-grade">
        <div className="flex flex-col items-end gap-space-1">
          <GradeChip
            grade={health?.grade}
            disciplines={health?.disciplines}
            size="lg"
            showRubric
            loading={healthLoading}
            data-testid="vital-grade-chip"
          />
          <button
            type="button"
            className="text-caption text-gold-shadow hover:underline"
            onClick={() =>
              document
                .getElementById("health-scorecard")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            data-testid="vital-grade-scorecard-link"
          >
            View scorecard ↓
          </button>
        </div>
      </VitalTile>

      <VitalTile label="Current balance" testId="vital-balance">
        <span className={`${valueSizeClass(balanceText)} font-bold tabular-nums text-onyx`}>
          {balanceText}
        </span>
      </VitalTile>

      <VitalTile label="Next payment due" testId="vital-next-payment">
        {ledgerLoading ? (
          <Skeleton className="h-6 w-28" />
        ) : ledgerSummary === null ? (
          <span className="text-small text-gray-mid">Not available</span>
        ) : ledgerSummary?.nextDueDate ? (
          <>
            <span
              className={`${valueSizeClass(formatCalendarDate(ledgerSummary.nextDueDate))} font-bold tabular-nums text-onyx`}
            >
              {formatCalendarDate(ledgerSummary.nextDueDate)}
            </span>
            <span className="text-caption text-gray-mid block">
              {nextDueLabel(ledgerSummary.nextDueDate)}
            </span>
          </>
        ) : (
          <span className="text-small text-gray-mid">None scheduled</span>
        )}
      </VitalTile>

      <VitalTile label="Latest DSCR" testId="vital-dscr">
        {loan.latestDscr != null ? (
          <>
            <span
              className={`${valueSizeClass(loan.latestDscr.toFixed(2))} font-bold tabular-nums ${dscrClass}`}
            >
              {loan.latestDscr.toFixed(2)}
            </span>
            {loan.latestDscrStatus && (
              <span className={`text-caption block ${dscrClass}`}>
                {loan.latestDscrStatus}
              </span>
            )}
          </>
        ) : (
          <span className="text-small text-gray-mid">None recorded</span>
        )}
      </VitalTile>

      <VitalTile label="Days delinquent" testId="vital-days-delinquent">
        <span className={`${valueSizeClass(daysText)} font-bold tabular-nums ${delinquentClass}`}>
          {daysText}
        </span>
      </VitalTile>

      <VitalTile label="Account state" testId="vital-account-state">
        {loan.accountState ? (
          <StatusBadge tone={accountStateTone(loan.accountState)}>
            {loan.accountState}
          </StatusBadge>
        ) : (
          <span className="text-small text-gray-mid">—</span>
        )}
      </VitalTile>

      <VitalTile label="Maturity" testId="vital-maturity">
        <span className={`${valueSizeClass(monthsText)} font-bold tabular-nums text-onyx`}>
          {monthsText}
        </span>
        <span className="text-caption text-gray-mid block">
          month{monthsToMaturity === 1 ? "" : "s"} to balloon ·{" "}
          {formatCalendarDate(loan.maturityDate)}
        </span>
      </VitalTile>
    </StatsBar>
  );
}

function VitalTile({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  // A `StatCard`, so the strip's chrome is the shared one (§12). The tile's
  // CONTENT is untouched — every vital renders exactly what it rendered before.
  return <StatCard label={label} value={children} data-testid={testId} />;
}

// `tz` REQUIRED: this label is the whole tile, and its only caller is the
// component above, which has the viewer's zone in hand.
function nextDueLabel(iso: string): string {
  const days = daysUntil(iso);
  if (days === null) return "";
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  if (days === 0) return "due today";
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

function monthsBetween(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

function accountStateTone(
  state: string,
): "ok" | "warn" | "danger" | "neutral" {
  const s = state.toLowerCase();
  if (s.includes("perform") && !s.includes("non")) return "ok";
  if (s.includes("default") || s.includes("non-perform")) return "danger";
  if (s.includes("watch") || s.includes("delinq")) return "warn";
  return "neutral";
}
